import clsx from 'clsx'
import React, { forwardRef, useId, useMemo, useState } from 'react'

import { SolidAlertTriangleIcon } from 'slab/icons/SolidAlertTriangle'

import { Content } from './Content'
import { FileUploadContext } from './context'

interface FileUploadProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children' | 'className' | 'size' | 'type'> {
  accept?: string;
  children: React.ReactNode | ((props: { dropping: boolean, dropped: boolean }) => React.ReactNode);
  description?: React.ReactNode;
  error?: React.ReactNode;
  height?: 'full' | 'auto';
  label?: React.ReactNode;
  multiple?: boolean;
}

const FileInput = forwardRef<HTMLInputElement, FileUploadProps>(({
  accept,
  children,
  description,
  error,
  height = 'auto',
  id,
  label,
  multiple = false,
  ...props
}, forwardedRef) => {
  const [dropping, setDropping] = useState(false)

  // We're using this `dropped` state to prevent the hover state from appearing
  // immediately after a file is dropped. It will be reset when the mouse leaves
  // the drop area.
  const [dropped, setDropped] = useState(false)

  const autogeneratedId = useId()
  const descriptionId = useId()
  const errorMessageId = useId()

  const inputId = id || autogeneratedId

  const describedBy = useMemo(
    () =>
      [
        description && descriptionId,
        error && errorMessageId,
      ]
        .filter(Boolean)
        .join(' '),
    [error, description]
  )

  const contextValue = useMemo(() => ({ dropping, dropped }), [dropping, dropped])

  function renderChildren () {
    if (typeof children === 'function') {
      return children({ dropping, dropped })
    }

    return children
  }

  function renderDescription () {
    if (!description) return null

    return (
      <div className='body-sm text-gray-500 mt-1' id={descriptionId}>
        {description}
      </div>
    )
  }

  function renderErrorMessage () {
    if (!error || typeof error === 'boolean') return null

    return (
      <div className='flex gap-x-1 mt-1' id={errorMessageId}>
        <div className='text-red-400'>
          <SolidAlertTriangleIcon size={16} />
        </div>
        <div className='body-sm-bold text-red-700'>
          {error}
        </div>
      </div>
    )
  }

  return (
    <div>
      {!!label && (
        <label
          className={clsx('body-sm-bold block mb-2', {
            'text-red-700': error,
            'text-gray-800': !error,
          })}
          htmlFor={inputId}
        >
          {label}
        </label>
      )}
      <div
        className={clsx(
          'relative border border-dashed rounded-lg transition cursor-pointer outline outline-2 outline-offset-2 outline-transparent has-[:focus-visible]:outline-thatch-500',
          {
            'bg-thatch-50 border-thatch-400': dropping,
            'bg-gray-50 has-[:focus-visible]:bg-gray-100 border-gray-400 has-[[aria-invalid="true"]]:border-red-300 has-[:invalid]:border-red-300 ':
              !dropping,
            'hover:bg-gray-100': !dropping && !dropped,
            'h-full': height === 'full',
          }
        )}
      >
        <input
          {...props}
          aria-describedby={describedBy}
          aria-invalid={!!error}
          accept={accept}
          className='opacity-0 absolute top-0 left-0 w-full h-full cursor-pointer'
          id={inputId}
          multiple={multiple}
          onDragEnter={(event) => {
            setDropping(true)
            props.onDragEnter?.(event)
          }}
          onDragLeave={(event) => {
            setDropping(false)
            props.onDragLeave?.(event)
          }}
          onDrop={(event) => {
            setDropping(false)
            setDropped(true)
            props.onDrop?.(event)
          }}
          onMouseLeave={(event) => {
            setDropped(false)
            props.onMouseLeave?.(event)
          }}
          ref={forwardedRef}
          type='file'
        />
        <FileUploadContext.Provider value={contextValue}>
          {renderChildren()}
        </FileUploadContext.Provider>
      </div>
      {renderErrorMessage()}
      {renderDescription()}
    </div>
  )
})

export const FileUpload = Object.assign(FileInput, { Content })
